/* eslint-disable factorial/no-raw-html */
import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'
import {
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Folder as FolderIcon,
  InfoCircle,
} from '@factorialco/f0-react/icons/app'
import * as React from 'react'
import { createPortal } from 'react-dom'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import i18n from 'modules/core/lib/i18n'

import getTitle from 'modules/documents/components/Documents/getTitle'
import { Folder } from 'modules/documents/hooks/useGetFolders'
import { sortFoldersByName } from 'modules/documents/lib/Folders'
import { spaceToDescription, spacesFromFolders } from 'modules/documents/lib/Spaces'
import { SPACE, SPACES } from 'modules/documents/lib/constants'

import './index.styles.css'

const ALL_SPACES = SPACES.UNKNOWN
const NO_FOLDER_FILTER_ID = '-1'

interface FolderInfo {
  id: string
  name: string
  parentFolderId?: string
  directChildren?: string[]
}

interface Props {
  label?: string
  readOnly?: boolean
  noneOption?: boolean
  initialFolder?: Folder
  initialSpace?: SPACE
  folders: Folder[]
  onChange: (value: string | null) => void
  hideSelectInput?: boolean
  tooltip?: string
}

const FolderSelectorF0 = ({
  label,
  readOnly = false,
  noneOption = true,
  initialFolder,
  initialSpace,
  folders,
  onChange,
  hideSelectInput = false,
  tooltip,
}: Props) => {
  const initialTextLabel = initialFolder
    ? getTitle(initialFolder.name)
    : i18n.t('split_documents.form.none')

  const [isOpen, setIsOpen] = useState(false)
  const [selectButtonDisabled, setSelectButtonDisabled] = useState<boolean>(true)
  const [textLabel, setTextLabel] = useState<string>(initialTextLabel)
  const [selectedFolder, setSelectedFolder] = useState<FolderInfo | null>(null)
  const [visitedFolder, setVisitedFolder] = useState<FolderInfo | null>(null)
  const [foldersToDisplay, setFoldersToDisplay] = useState<Map<string, FolderInfo>>(new Map())
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({
    top: 0,
    left: 0,
    width: 0,
  })

  const popoverRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const allFolders = useLazyRef<Map<string, FolderInfo>>(
    () => new Map<string, FolderInfo>()
  ).current
  const rootFolders = useLazyRef<Map<string, FolderInfo>>(
    () => new Map<string, FolderInfo>()
  ).current

  const spacesToShow = spacesFromFolders(initialFolder ? [initialFolder] : folders)
  const showSpacesLevel = spacesToShow.length > 1
  const initSpace = initialSpace || (showSpacesLevel ? ALL_SPACES : spacesToShow[0] || ALL_SPACES)
  const [currentSpace, setCurrentSpace] = useState<SPACE>(initSpace)
  const spaceDescriptionText = spaceToDescription(currentSpace)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // eslint-disable-next-line no-type-assertion/no-type-assertion
      const target = event.target as Node
      const insideWrapper = popoverRef.current?.contains(target)
      const insideDropdown = dropdownRef.current?.contains(target)
      if (!insideWrapper && !insideDropdown && isOpen) {
        setIsOpen(false)
        initialSetup()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  useUnsafeEffect(() => {
    if (currentSpace === ALL_SPACES) {
      allFolders.clear()
      rootFolders.clear()
      return
    }

    if (noneOption) {
      const noneFolder = {
        id: NO_FOLDER_FILTER_ID,
        name: i18n.t('split_documents.form.none'),
      }
      allFolders.set(NO_FOLDER_FILTER_ID, noneFolder)
      rootFolders.set(NO_FOLDER_FILTER_ID, noneFolder)
    }

    const filteredFoldersBySpace = folders.filter((folder) => folder.space === currentSpace)
    const sortedFolders = sortFoldersByName(filteredFoldersBySpace)

    sortedFolders.forEach((folder) => {
      const directSubfoldersIds = filteredFoldersBySpace
        .filter((subfolder) => subfolder.parentFolderId === folder.id)
        .map((subfolder) => subfolder.id)

      const folderInfo = {
        id: folder.id,
        name: folder.name,
        parentFolderId: folder.parentFolderId ?? undefined,
        directChildren: directSubfoldersIds,
      }

      allFolders.set(folder.id, folderInfo)

      if (!folder.parentFolderId) {
        rootFolders.set(folder.id, folderInfo)
      }
    })

    setFoldersToDisplay(rootFolders)
    setSelectedFolder(null)
    setVisitedFolder(null)
    initialSetup()
  }, [folders, initialFolder, currentSpace])

  const initialSetup = () => {
    if (!initialFolder) return
    setSelectedFolder(allFolders.get(initialFolder.id) ?? null)

    if (!initialFolder.parentFolderId) {
      setFoldersToDisplay(rootFolders)
      return
    }

    const foldersToShow = new Map<string, FolderInfo>()
    const parentFolder = allFolders.get(initialFolder.parentFolderId)
    const directChildrenIds = parentFolder?.directChildren || []

    directChildrenIds.forEach((id) => {
      const folderInfo = allFolders.get(id)
      if (!folderInfo) return
      foldersToShow.set(id, folderInfo)
    })

    setFoldersToDisplay(foldersToShow)

    if (parentFolder) {
      setVisitedFolder(parentFolder)
    }
  }

  const onSelectFolder = (folder: FolderInfo) => {
    if (selectedFolder?.id === folder.id) {
      setSelectedFolder(null)
      setSelectButtonDisabled(true)
    } else {
      setSelectedFolder(folder)
      setSelectButtonDisabled(false)
    }
  }

  const confirmFolderSelection = () => {
    if (!selectedFolder) return

    const selectedFolderId = selectedFolder.id === NO_FOLDER_FILTER_ID ? null : selectedFolder.id

    setIsOpen(false)
    setTextLabel(getTitle(selectedFolder.name))
    setSelectButtonDisabled(true)
    setFoldersToDisplay(rootFolders)
    setSelectedFolder(null)
    setVisitedFolder(null)
    onChange(selectedFolderId)
  }

  const retrieveFoldersInfo = (folderIds: string[]) => {
    const foldersMap = new Map<string, FolderInfo>()
    folderIds.forEach((id) => {
      const folderInfo = allFolders.get(id)
      if (!folderInfo) return
      foldersMap.set(id, folderInfo)
    })
    return foldersMap
  }

  const updateFolders = (folder: FolderInfo) => {
    setSelectButtonDisabled(true)
    setSelectedFolder(null)
    setVisitedFolder(folder)
    const directChildrenIds = allFolders.get(folder.id)?.directChildren || []
    setFoldersToDisplay(retrieveFoldersInfo(directChildrenIds))
  }

  const hasChildren = (value: FolderInfo) => !!value.directChildren?.length

  const goBack = () => {
    setSelectedFolder(null)

    if (!visitedFolder) {
      setFoldersToDisplay(new Map())
      setCurrentSpace(ALL_SPACES)
    }

    if (visitedFolder) {
      setSelectButtonDisabled(true)
      const immediateParentId = visitedFolder.parentFolderId
      if (!immediateParentId) {
        setFoldersToDisplay(rootFolders)
        setVisitedFolder(null)
        return
      }

      const immediateParent = allFolders.get(immediateParentId)
      if (immediateParent) {
        setVisitedFolder(immediateParent)
        const folderIds = immediateParent.directChildren || []
        setFoldersToDisplay(retrieveFoldersInfo(folderIds))
      }
    }
  }

  const visitedFolderParentText = () => {
    if (!visitedFolder) return i18n.t('split_documents.form.back')

    if (!visitedFolder.parentFolderId) return spaceDescriptionText

    const immediateParentId = visitedFolder.parentFolderId
    const immediateParent = allFolders.get(immediateParentId)
    if (immediateParent) return getTitle(immediateParent.name)
    return spaceDescriptionText
  }

  const selectFolderText = () => {
    if (selectedFolder) {
      return i18n.t('split_documents.form.select_folder_name', {
        folder_name: getTitle(selectedFolder.name),
      })
    }
    return i18n.t('split_documents.form.select_folder')
  }

  const visitedFolderText = visitedFolder ? getTitle(visitedFolder.name) : spaceDescriptionText

  const folderNavigation = (
    <>
      <div className="FolderSelectorF0_header">
        <F0Button
          variant="ghost"
          size="md"
          icon={ChevronLeft}
          label={visitedFolderParentText()}
          onClick={goBack}
          disabled={!visitedFolder && (!showSpacesLevel || initialSpace === currentSpace)}
        />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: 'var(--spacing-s4)',
          }}
        >
          <F0Icon icon={FolderIcon} size="sm" />
          <F0Text variant="body" content={visitedFolderText} />
        </div>
      </div>

      <ul className="FolderSelectorF0_list">
        {!foldersToDisplay.size &&
          showSpacesLevel &&
          spacesToShow.map((spaceToShow) => (
            <li key={spaceToShow} className="FolderSelectorF0_item">
              <button
                type="button"
                className="FolderSelectorF0_itemButton"
                onClick={() => setCurrentSpace(spaceToShow)}
              >
                <F0Text variant="body" content={spaceToDescription(spaceToShow)} />
              </button>
              <button
                type="button"
                className="FolderSelectorF0_chevron"
                onClick={() => setCurrentSpace(spaceToShow)}
              >
                <F0Icon icon={ChevronRight} size="sm" />
              </button>
            </li>
          ))}
        {[...foldersToDisplay.values()].map((folder) => {
          const isSelected = selectedFolder?.id === folder.id
          return (
            <li
              key={folder.id}
              className={`FolderSelectorF0_item ${isSelected ? 'FolderSelectorF0_item--selected' : ''}`}
            >
              <button
                type="button"
                className="FolderSelectorF0_itemButton"
                onClick={() => onSelectFolder(folder)}
              >
                <F0Text variant="body" content={getTitle(folder.name)} />
              </button>
              {isSelected && (
                <span className="FolderSelectorF0_checkmark">
                  <F0Icon icon={Check} size="sm" />
                </span>
              )}
              {hasChildren(folder) && (
                <button
                  type="button"
                  className="FolderSelectorF0_chevron"
                  onClick={() => updateFolders(folder)}
                  aria-label={i18n.t('split_documents.form.show_subfolders')}
                >
                  <F0Icon icon={ChevronRight} size="sm" />
                </button>
              )}
            </li>
          )
        })}
      </ul>

      <button
        type="button"
        className="FolderSelectorF0_confirm"
        onClick={confirmFolderSelection}
        disabled={selectButtonDisabled}
      >
        <F0Text variant="label" content={selectFolderText()} />
      </button>
    </>
  )

  const openDropdown = useCallback(() => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setDropdownPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      })
    }
    setIsOpen(!isOpen)
  }, [isOpen])

  if (readOnly || (foldersToDisplay.size === 0 && !showSpacesLevel)) {
    return (
      <F0Box gap="xs">
        {label && <F0Text content={label} />}
        <F0Text variant="body" content={textLabel} />
      </F0Box>
    )
  }

  if (hideSelectInput) return <div className="FolderSelectorF0_dropdown">{folderNavigation}</div>

  return (
    <F0Box ref={popoverRef} flexDirection="column">
      <F0Box display="flex" gap="md" flexDirection="column">
        {label && (
          <F0Box flexDirection="row" gap="sm">
            <span className="FolderSelectorF0_label">{label}</span>
            {tooltip && (
              <span title={tooltip}>
                <F0Icon icon={InfoCircle} size="sm" />
              </span>
            )}
          </F0Box>
        )}
        <button
          ref={triggerRef}
          type="button"
          className="FolderSelectorF0_trigger"
          onClick={openDropdown}
        >
          <F0Text variant="body" content={textLabel} />
          <F0Icon icon={isOpen ? ChevronUp : ChevronDown} size="sm" />
        </button>
      </F0Box>
      {isOpen &&
        createPortal(
          <div
            ref={dropdownRef}
            className="FolderSelectorF0_dropdown"
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 10000,
            }}
          >
            {folderNavigation}
          </div>,
          document.body
        )}
    </F0Box>
  )
}

export default FolderSelectorF0
